import { InputAdornment, MenuItem, Select, TextField, Typography } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
	defaultCountries,
	FlagImage,
	parseCountry,
	usePhoneInput
} from "react-international-phone";
import "react-international-phone/style.css";

interface PhoneInputProps {
	value: string;
	onChange: (phone: string) => void;
	hideLabel?: boolean;
	[key: string]: any;
	required?: boolean;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
	value,
	onChange,
	hideLabel = false,
	required = false,
	...restProps
}) => {
	const { t } = useTranslation();
	const previousValue = useRef(value);
	const isExternalUpdate = useRef(false);

	// Detect when value is updated externally
	useEffect(() => {
		if (value !== previousValue.current) {
			isExternalUpdate.current = true;
			previousValue.current = value;
		}
	}, [value]);

	const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
		usePhoneInput({
			defaultCountry: "fr",
			value,
			countries: defaultCountries,
			onChange: data => {
				if (data.phone === "+33") return;

				// Ignore the onChange if it's triggered by an external value update
				if (isExternalUpdate.current) {
					isExternalUpdate.current = false;
					return;
				}

				previousValue.current = data.phone;
				onChange(data.phone);
			}
		});

	const getLabel = () => {
		if (hideLabel) return "";
		const baseLabel = t("phoneInput.label");
		return required ? `${baseLabel} *` : baseLabel;
	};

	return (
		<TextField
			variant="outlined"
			label={getLabel()}
			placeholder={t("phoneInput.placeholder")}
			value={inputValue}
			onChange={handlePhoneValueChange}
			type="tel"
			inputRef={inputRef}
			fullWidth
			slotProps={{
				input: {
					startAdornment: (
						<InputAdornment
							position="start"
							style={{ marginRight: "2px", marginLeft: "-8px" }}
						>
							<Select
								MenuProps={{
									style: {
										height: "300px",
										width: "360px",
										top: "10px",
										left: "-34px"
									},
									transformOrigin: {
										vertical: "top",
										horizontal: "left"
									}
								}}
								sx={{
									width: "max-content",
									// Remove default outline (display only on focus)
									fieldset: {
										display: "none"
									},
									'&.Mui-focused:has(div[aria-expanded="false"])': {
										fieldset: {
											display: "block"
										}
									},
									// Update default spacing
									".MuiSelect-select": {
										padding: "8px",
										paddingRight: "24px !important"
									},
									svg: {
										right: 0
									}
								}}
								value={country.iso2}
								onChange={e => setCountry(e.target.value)}
								renderValue={value => (
									<FlagImage iso2={value} style={{ display: "flex" }} />
								)}
							>
								{defaultCountries.map(c => {
									const country = parseCountry(c);
									return (
										<MenuItem key={country.iso2} value={country.iso2}>
											<FlagImage iso2={country.iso2} style={{ marginRight: "8px" }} />
											<Typography marginRight="8px">{country.name}</Typography>
											<Typography color="gray">+{country.dialCode}</Typography>
										</MenuItem>
									);
								})}
							</Select>
						</InputAdornment>
					)
				}
			}}
			{...restProps}
		/>
	);
};

export default PhoneInput;
